/**
 * Everything the credit-lots card decides, kept out of JSX so it can be tested
 * — the `web-logic` vitest project is node-only and has no DOM.
 */

export type WalletLotStatus = 'expired' | 'spent' | 'expiring' | 'active';

/** The wire shape of one lot, mirroring the server's `WalletLotRow`. The two
 *  projects cannot import each other; `remainingMicroUsd` is optional here for
 *  the same reason it is optional there — see `hasRemainders`. */
export interface WalletLotRow {
  id: string;
  amountMicroUsd: number;
  createdAt: number;
  expiresAt: number;
  expiredAt: number | null;
  status: WalletLotStatus;
  remainingMicroUsd?: number;
}

export interface WalletLotsResponse {
  lots: WalletLotRow[];
  totalRemainingMicroUsd?: number;
}

/**
 * How much of a lot has been spent, or null when that is unknowable.
 *
 * The server omits `remainingMicroUsd` when a subject's lots, ledger and
 * wallet scalar disagree, because every derived remainder is then wrong.
 * Substituting 0 here would claim the whole credit was spent; substituting the
 * full amount would claim none of it was. Both are wrong numbers about
 * somebody's money, so this returns null and the column is not rendered.
 */
export function usedMicroUsd(lot: WalletLotRow): number | null {
  if (lot.remainingMicroUsd === undefined) return null;
  return lot.amountMicroUsd - lot.remainingMicroUsd;
}

/** Whether the used/remaining columns can be shown at all. One lot without a
 *  remainder means the whole response came from an inconsistent state, so the
 *  columns go for every row rather than gapping some of them. */
export function hasRemainders(lots: WalletLotRow[]): boolean {
  return lots.every((l) => l.remainingMicroUsd !== undefined);
}

export function lotStatusKey(status: WalletLotStatus): string {
  return `dashboard.wallet.lots.status.${status}`;
}
